import React, { useContext, useEffect, useState } from "react";
import NavBar from "./NavBar";
import Footer from "./Footer";
import { ShieldPlus } from "lucide-react";
import axios from "axios";
import Loading from "./Loading";
import { AuthContext } from "../Contexts/AuthContext";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../services/api";
import { normalizeAdminPermissions } from "../utils/adminPermissions";

export default function Funcionarios() {
  const [colaboradores, setColaboradores] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erroCarregamento, setErroCarregamento] = useState("");
  const { usuario } = useContext(AuthContext);
  const permissoes = normalizeAdminPermissions(usuario);
  const superAdmin = usuario?.tipoUsuario === "super";
  const podeAtivar =
    superAdmin || permissoes.podeAtivarClientes;

  function clienteEstaAtivo(cliente) {
    const profile = String(cliente?.profile || "").toUpperCase();

    return (
      String(cliente?.disabled || "false").toLowerCase() !== "true" &&
      profile !== "DESATVADO" &&
      profile !== "DESATIVADO"
    );
  }

  function clienteEstaDesativado(cliente) {
    return !clienteEstaAtivo(cliente);
  }

  function escapeHtml(valor) {
    return String(valor || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function normalizarPlanos(respostas) {
    const todos = respostas.flatMap((resposta) =>
      resposta.status === "fulfilled" && Array.isArray(resposta.value.data)
        ? resposta.value.data
        : []
    );
    const unicos = new Map();

    todos.forEach((plano) => {
      const nome = String(plano.plano || "").trim();
      if (!nome) return;
      unicos.set(nome, {
        nome,
        preco: plano.preco,
      });
    });

    return [...unicos.values()].sort((a, b) => a.nome.localeCompare(b.nome));
  }

  async function mostrarColaboradores() {
    try {
      setLoading(true);
      setErroCarregamento("");
      const token = localStorage.getItem("token");

      if (!token) {
        setColaboradores([]);
        setErroCarregamento("Sessao expirada. Faca login novamente.");
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      const [
        dados,
        planosResidenciais,
        planosEmpresas,
        planosResidenciaisTri,
        planosEmpresasTri,
      ] = await Promise.allSettled([
        axios.get(
          `${API_BASE_URL}/usuariosids?somenteDesativados=${
            superAdmin ? "false" : "true"
          }`,
          { headers }
        ),
        axios.get(`${API_BASE_URL}/planos`, { headers }),
        axios.get(`${API_BASE_URL}/planosempresariais`, { headers }),
        axios.get(`${API_BASE_URL}/planostrimestraisresidenciais`, { headers }),
        axios.get(`${API_BASE_URL}/planostrimestraisempresariais`, { headers }),
      ]);

      if (dados.status !== "fulfilled") {
        throw dados.reason;
      }

      setColaboradores(
        Array.isArray(dados.value.data)
          ? dados.value.data.filter((cliente) =>
              superAdmin ? true : clienteEstaDesativado(cliente)
            )
          : []
      );
      setPlanos(
        normalizarPlanos([
          planosResidenciais,
          planosEmpresas,
          planosResidenciaisTri,
          planosEmpresasTri,
        ])
      );
    } catch (erro) {
      console.log(erro);
      const status = erro.response?.status;
      const mensagemApi = erro.response?.data?.mensagem;

      if (status === 401) {
        setErroCarregamento("Sessao expirada. Faca login novamente.");
      } else if (status === 403) {
        setErroCarregamento(
          mensagemApi || "Este admin nao tem permissao para ver estes clientes."
        );
      } else {
        setErroCarregamento(
          mensagemApi || "Nao foi possivel carregar os clientes agora."
        );
      }

      setColaboradores([]);
    } finally {
      setLoading(false);
    }
  }

  async function habilitarColaborador(id) {
    try {
      const limiteDias =
        superAdmin ? null : permissoes.limiteDiasAtivacao || 5;
      const cliente = colaboradores.find(
        (colaborador) => Number(colaborador.idUsuario) === Number(id)
      );
      const opcoesPlanos = planos.length
        ? planos
            .map(
              (plano) =>
                `<option value="${escapeHtml(plano.nome)}">${escapeHtml(
                  `${plano.nome}${plano.preco ? ` - ${plano.preco} MZN` : ""}`
                )}</option>`
            )
            .join("")
        : `<option value="${escapeHtml(
            cliente?.profileusuario || "PLANO-6Mbps"
          )}">${escapeHtml(cliente?.profileusuario || "PLANO-6Mbps")}</option>`;
      const resultado = await Swal.fire({
        title: "Activar cliente",
        html: `
          <select id="plano" class="swal2-input">
            <option value="">Escolha o plano</option>
            ${opcoesPlanos}
          </select>
          <input id="dias" type="number" min="1" ${
            limiteDias ? `max="${limiteDias}"` : ""
          } value="1" class="swal2-input" placeholder="Dias">
          <textarea id="observacao" class="swal2-textarea" placeholder="Motivo da activacao"></textarea>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Activar",
        cancelButtonText: "Cancelar",
        preConfirm: () => {
          const dias = Number(document.getElementById("dias")?.value);
          const profile = document.getElementById("plano")?.value?.trim();
          const observacao = document.getElementById("observacao")?.value?.trim();

          if (!profile) {
            Swal.showValidationMessage("Escolha o plano do cliente.");
            return false;
          }

          if (!Number.isInteger(dias) || dias < 1) {
            Swal.showValidationMessage("Informe um numero inteiro de dias maior que 0.");
            return false;
          }

          if (!superAdmin && dias > limiteDias) {
            Swal.showValidationMessage(`Informe dias entre 1 e ${limiteDias}.`);
            return false;
          }

          if (!observacao) {
            Swal.showValidationMessage("A observacao e obrigatoria.");
            return false;
          }

          return { dias, observacao, profile };
        },
      });

      if (!resultado.isConfirmed) return;

      const token = localStorage.getItem("token");
      const resposta = await axios.post(
        `${API_BASE_URL}/clientes/${id}/ativacao-extra`,
        resultado.value,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      await Swal.fire({
        title: "Cliente activo",
        text: `Foram concedidos ${resposta.data.usuario.diasConcedidos} dias no plano ${resposta.data.usuario.profile}. O token ficou com ${resposta.data.usuario.diasToken} dias.`,
        icon: "success",
      });

      await mostrarColaboradores();
    } catch (erro) {
      const mensagem =
        erro.response?.data?.mensagem || "Nao foi possivel activar este cliente.";
      Swal.fire("Erro", mensagem, "error");
    }
  }

  useEffect(() => {
    mostrarColaboradores();
  }, []);

  const colaboradoresOrdenados = [...colaboradores].sort((a, b) =>
    String(a.name || "").localeCompare(b.name || "")
  );
  const totalColunas = podeAtivar ? 5 : 4;

  return (
    <div className="flex min-h-screen flex-col">
      <div className="sticky top-0 z-10 bg-blue-500">
        <NavBar />
      </div>

      {loading ? (
        <Loading />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex justify-center px-2 py-6 sm:px-4"
        >
          <div className="w-full max-w-7xl rounded-md border shadow-md">
            <div className="border-b bg-slate-50 px-4 py-3">
              <h1 className="text-lg font-semibold text-slate-900">
                Activar cliente
              </h1>
              <p className="text-sm text-slate-500">
                {superAdmin
                  ? "Super admin pode adicionar dias a clientes activos ou desativados."
                  : "Apenas clientes desativados aparecem aqui."}{" "}
                Escolha o cliente, informe os dias e escreva a observacao obrigatoria.
              </p>
            </div>
            {erroCarregamento && (
              <div className="m-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span>{erroCarregamento}</span>
                  <button
                    type="button"
                    onClick={mostrarColaboradores}
                    className="rounded bg-red-600 px-3 py-2 font-semibold text-white hover:bg-red-700"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="border px-3 py-2 text-center">Ordem</th>
                    <th className="border px-3 py-2 text-center">Nome</th>
                    <th className="border px-3 py-2 text-center">POP</th>
                    <th className="border px-3 py-2 text-center">Estado</th>
                    {podeAtivar && (
                      <th className="border px-3 py-2 text-center">Activar</th>
                    )}
                  </tr>
                </thead>

                <motion.tbody
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.05,
                      },
                    },
                  }}
                >
                  {colaboradoresOrdenados.length > 0 ? (
                    colaboradoresOrdenados.map((colaborador, i) => (
                      <motion.tr
                        key={colaborador.idUsuario || i}
                        variants={{
                          hidden: { opacity: 0, y: 10 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        className="hover:bg-gray-100"
                      >
                        <td className="border px-3 py-2 text-center">{i + 1}</td>
                        <td className="border px-3 py-2 text-center">
                          {colaborador.name}
                        </td>
                        <td className="border px-3 py-2 text-center">
                          {colaborador.pop || "Sem POP"}
                        </td>
                        <td className="border px-3 py-2 text-center">
                          {clienteEstaAtivo(colaborador) ? "Activo" : "Desativado"}
                        </td>
                        {podeAtivar && (
                          <td className="border px-3 py-2 text-center">
                            <motion.div
                              whileHover={{ scale: 1.2 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <ShieldPlus
                                className="mx-auto h-6 w-6 cursor-pointer hover:text-blue-500"
                                onClick={() =>
                                  habilitarColaborador(colaborador.idUsuario)
                                }
                              />
                            </motion.div>
                          </td>
                        )}
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={totalColunas}
                        className="border px-3 py-8 text-center text-slate-500"
                      >
                        {erroCarregamento
                          ? "Corrija o acesso/API e tente novamente."
                          : superAdmin
                            ? "Nenhum cliente encontrado."
                            : "Nenhum cliente desativado encontrado para os privilegios deste admin."}
                      </td>
                    </tr>
                  )}
                </motion.tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      <div className="mt-auto bg-blue-500">
        <Footer />
      </div>
    </div>
  );
}
